import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { processingApi, tamsApi } from '@/api/client';
import type {
  ApproveClipRequest,
  CreateJobRequest,
  JobListParams,
  ProcessingSettings,
  ResultKind,
} from '@/types/processing';

// Query keys — one root so a write can invalidate the whole area.
export const processingKeys = {
  all: ['processing'] as const,
  recordings: (eventId?: string) => ['processing', 'recordings', eventId ?? null] as const,
  recording: (sourceId: string) => ['processing', 'recording', sourceId] as const,
  jobs: (params?: JobListParams) => ['processing', 'jobs', params ?? {}] as const,
  results: (streamId: string, kind?: ResultKind) =>
    ['processing', 'results', streamId, kind ?? null] as const,
  clips: (streamId?: string) => ['processing', 'clips', streamId ?? null] as const,
  settings: ['processing', 'settings'] as const,
  tamsFlows: (sourceId: string, kind?: string) => ['tams', 'flows', sourceId, kind ?? null] as const,
  tamsSegments: (flowId: string, timerange?: string, limit?: number) =>
    ['tams', 'segments', flowId, timerange ?? null, limit ?? null] as const,
};

const JOB_POLL_MS = 5_000;
const RESULT_POLL_MS = 15_000;

export function useRecordings(eventId?: string) {
  return useQuery({
    queryKey: processingKeys.recordings(eventId),
    queryFn: () => processingApi.listRecordings(eventId ? { eventId } : undefined),
    select: (r) => r.recordings,
    refetchInterval: RESULT_POLL_MS,
  });
}

export function useRecording(sourceId: string | undefined) {
  return useQuery({
    queryKey: processingKeys.recording(sourceId ?? ''),
    queryFn: () => processingApi.getRecording(sourceId!),
    enabled: !!sourceId,
    refetchInterval: RESULT_POLL_MS,
  });
}

export function useJobs(params?: JobListParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: processingKeys.jobs(params),
    queryFn: () => processingApi.listJobs(params),
    refetchInterval: JOB_POLL_MS,
    enabled: options?.enabled ?? true,
  });
}

export function useResults(streamId: string | undefined, kind?: ResultKind) {
  return useQuery({
    queryKey: processingKeys.results(streamId ?? '', kind),
    queryFn: () => processingApi.listResults({ streamId: streamId!, kind }),
    select: (r) => r.results,
    enabled: !!streamId,
    refetchInterval: RESULT_POLL_MS,
  });
}

export function useClips(streamId?: string) {
  return useQuery({
    queryKey: processingKeys.clips(streamId),
    queryFn: () => processingApi.listClips(streamId ? { streamId } : undefined),
    select: (r) => r.clips,
    refetchInterval: JOB_POLL_MS,
  });
}

export function useProcessingSettings() {
  return useQuery({
    queryKey: processingKeys.settings,
    queryFn: () => processingApi.getSettings(),
  });
}

export function useTamsFlows(sourceId: string | undefined, kind?: string) {
  return useQuery({
    queryKey: processingKeys.tamsFlows(sourceId ?? '', kind),
    queryFn: () => tamsApi.listFlows({ sourceId: sourceId!, kind }),
    enabled: !!sourceId,
    staleTime: 60_000,
  });
}

/**
 * Presigned segment URLs expire (the spec's presigned GETs are short-lived),
 * so these are re-fetched well inside that window rather than cached.
 */
export function useTamsSegments(
  flowId: string | undefined,
  params?: { timerange?: string; limit?: number },
) {
  return useQuery({
    queryKey: processingKeys.tamsSegments(flowId ?? '', params?.timerange, params?.limit),
    queryFn: () => tamsApi.listSegments(flowId!, params),
    enabled: !!flowId,
    staleTime: 5 * 60_000,
  });
}

// ---- Mutations ----

function useInvalidateProcessing() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: processingKeys.all });
}

export function useCreateJob() {
  const invalidate = useInvalidateProcessing();
  return useMutation({
    mutationFn: (data: CreateJobRequest) => processingApi.createJob(data),
    onSuccess: invalidate,
  });
}

export function useJobActions() {
  const invalidate = useInvalidateProcessing();
  const cancel = useMutation({ mutationFn: processingApi.cancelJob, onSuccess: invalidate });
  const restart = useMutation({ mutationFn: processingApi.restartJob, onSuccess: invalidate });
  const remove = useMutation({ mutationFn: processingApi.deleteJob, onSuccess: invalidate });
  return { cancel, restart, remove };
}

export function useClipReview() {
  const invalidate = useInvalidateProcessing();
  const approve = useMutation({
    mutationFn: ({ jobId, data }: { jobId: string; data: ApproveClipRequest }) =>
      processingApi.approveClip(jobId, data),
    onSuccess: invalidate,
  });
  const reject = useMutation({ mutationFn: processingApi.rejectClip, onSuccess: invalidate });
  const remove = useMutation({ mutationFn: processingApi.deleteJob, onSuccess: invalidate });
  return { approve, reject, remove };
}

export function useUpdateProcessingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ProcessingSettings) => processingApi.updateSettings(data),
    onSuccess: (saved) => qc.setQueryData(processingKeys.settings, saved),
  });
}
